import React, { useState, useEffect } from "react";
import Header from "../../../components/Header/Header";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const L1AddIndent = () => {
  const [formData, setFormData] = useState({
    ProjectID: "",
    TypeID: "",
    ProjectName: "",
    L1A: false,
    L2: false,
    TaskSchedule: "",
  });
  const [projectOptions, setProjectOptions] = useState([]);
  const [stageOptions, setStageOptions] = useState([]);
  const [filteredStages, setFilteredStages] = useState([]);
  const [typeOptions, setTypeOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const role = useSelector((state) => state.auth.role);

  const enteredBy = useSelector((state) => state.auth.id);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch project options based on the user's role
        const projectResponse =
          role === "admin"
            ? await axios.get(`${API_BASE_URL}/dropdown/mprojects`)
            : await axios.get(`${API_BASE_URL}/dropdown/mprojects2`, {
                params: { ProjectIncharge: enteredBy },
              });

        // Fetch other dropdown data
        const [stages, types] = await Promise.all([
          axios.get(`${API_BASE_URL}/dropdown/mstages`),
          axios.get(`${API_BASE_URL}/dropdown/mindenttypes`),
        ]);

        // Set state for dropdown options
        setProjectOptions(projectResponse.data);
        setStageOptions(stages.data);
        setTypeOptions(types.data);

        // Find any available type to set as default
        const availableType = types.data[0];
        if (availableType) {
          setFormData(prev => ({
            ...prev,
            TypeID: availableType.TypeID.toString()
          }));
        }
      } catch (err) {
        console.error("Error fetching dropdown data:", err);
        setError("Failed to load dropdown data.");
      } finally {
        setLoading(false);
      }
    };

    if (enteredBy) fetchData();
  }, [enteredBy, role]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "ProjectID") {
      const projectStages = stageOptions.filter((stage) =>
        projectOptions.some(
          (project) =>
            project.ProjectID === value && project.StageID === stage.StageID
        )
      );
      setFilteredStages(projectStages);
      const defaultStage =
        projectStages.length > 0 ? projectStages[0].StageID : "";
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        StageID: defaultStage,
      }));
    } else {
      setFormData({
        ...formData,
        [name]: type === "checkbox" ? checked : value,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
  
    const selectedProject = projectOptions.find(p => p.ProjectID === parseInt(formData.ProjectID));
    const selectedType = typeOptions.find(t => t.TypeID.toString() === formData.TypeID);
  
    const dataToSend = {
      UserID: enteredBy,
      ProjectID: formData.ProjectID,
      ProjectName: selectedProject?.ProjectName || '',
      TypeID: formData.TypeID,
      CreationDate: new Date().toISOString().split("T")[0],
      Status: "Pending L1 Approval",
      L1A: formData.L1A,
      L2: formData.L2,
      TaskSchedule: formData.TaskSchedule,
    };
  
    try {
      const response = await axios.post(`${API_BASE_URL}/l1page/indent`, dataToSend);
  
      const newIndentRow = {
        IndentID: response.data.indentId,
        ProjectName: selectedProject?.ProjectName,
        TypeName: selectedType?.TypeName,
        CreationDate: dataToSend.CreationDate,
        Status: dataToSend.Status,
        L1: formData.L1 ? "Approved" : "Pending",
        L2: formData.L2 ? "Approved" : "Pending",
        PO: "Pending",
        TaskSchedule: dataToSend.TaskSchedule,
        ItemCount: 0,
      };
  
      navigate("/L1table", { state: { row: newIndentRow } });
    } catch (err) {
      console.error("Error adding indent:", err);
      setError(err.response?.data?.error || "Failed to add indent.");
    }
  };

  const handleCancel = () => {
    navigate("/l1page");
  };

  if (loading) return <div className="text-center py-4">Loading...</div>;

  return (
    <div>
      <Header />
      <div className="max-w-md mx-auto pt-8">
        <h1 className="text-4xl font-normal text-center mb-6">
          Add Indent
        </h1>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label
              htmlFor="ProjectID"
              className="block font-bold mb-1"
            >
              Project:
            </label>
            <select
              id="ProjectID"
              name="ProjectID"
              value={formData.ProjectID}
              onChange={handleInputChange}
              required
              className="w-full p-2 border border-gray-300 rounded"
            >
              <option value="">Select project</option>
              {projectOptions.map((option) => (
                <option key={option.ProjectID} value={option.ProjectID}>
                  {option.ProjectName}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-4">
            <label
              htmlFor="TypeID"
              className="block font-bold mb-1"
            >
              Type:
            </label>
            <select
              id="TypeID"
              name="TypeID"
              value={formData.TypeID}
              onChange={handleInputChange}
              required
              className="w-full p-2 border border-gray-300 rounded"
            >
              <option value="">Select type</option>
              {typeOptions.map((option) => (
                <option 
                  key={option.TypeID} 
                  value={option.TypeID}
                >
                  {option.TypeName}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-4">
            <label
              htmlFor="TaskSchedule"
              className="block font-bold mb-1"
            >
              Task Schedule:
            </label>
            <input
              type="text"
              id="TaskSchedule"
              name="TaskSchedule"
              value={formData.TaskSchedule}
              onChange={handleInputChange}
              className="w-full p-2 border border-gray-300 rounded"
            />
          </div>

          <div className="flex justify-between mt-6">
            <button
              type="submit"
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            >
              Submit
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
            >
              Cancel
            </button>
          </div>
        </form>
        {error && <p className="text-red-500 mt-4">{error}</p>}
      </div>
    </div>
  );
};

export default L1AddIndent;
